/**
 * PR4 (Auftrags-Dashboard, `docs/PLAN-EVENT-FELD-VERSIONIERUNG.md` Abschnitt 4.2, Mockup-Freigabe
 * 13.09.2026 "Mock up ist freigegeben"): reine Verdichtungsregeln fuer die ANZEIGE der
 * Vorgangsfelder-Versionierung (`GET /api/events/:id/field-versions`, Backend PR2/PR3 bereits
 * live). Die Datenbank bleibt lueckenlos ("Antoine: alles soll protokolliert werden") — verdichtet
 * wird ausschliesslich das, was `FeldVersionenDrawer`/`AuftragVerlaufKarte` daraus RENDERN.
 *
 * Vier Regeln aus dem Plan:
 *  A "Tippkorrektur-Kollaps" — Vorbild `fasseBurstsZusammen` (`lib/auftrag-verlauf.ts:240`), aber
 *    Schluessel = Feldname (nicht Aktion+Datensatz-ID), Zeitfenster 5 Minuten statt 60 Sekunden.
 *    Anders als dort werden die kollabierten Zwischenschritte NICHT verworfen, sondern bleiben
 *    ueber "N Zwischenschritte anzeigen" abrufbar (Plan: "lueckenlos" heisst nicht "verworfen").
 *  B "Erfassungsphase" — alles vor der ersten GESENDETEN Angebotsversion (`offer_history.sent_at`,
 *    `ui/auftrag/VersionVorschauDrawer.tsx::OfferHistoryEntry`) landet im Drawer standardmaessig
 *    eingeklappt.
 *  C "Buendelung fuer die Verlaufs-Karte" — gleicher Akteur, <= 30 Minuten Abstand -> EINE Zeile
 *    "Eckdaten geaendert · Gaeste, Datum · durch X". Die Erfassungsphase erzeugt dort NULL Zeilen
 *    (Regel B faengt sie vorher ab).
 *  D "Begrenzung" — 50 Zeilen im Drawer (nur der "danach"-Teil, die eingeklappte Erfassungsphase
 *    zaehlt nicht mit), danach ein "Aeltere anzeigen"-Knopf.
 *
 * Bewusst eine reine `lib/*`-Datei ohne React-Import (Grundregel dieses Repos, s. Kommentarkopf
 * `lib/auftrag-verlauf.ts`) — `FeldVersionenDrawer.tsx`/`AuftragVerlaufKarte.tsx` importieren nur
 * die fertigen Ergebnisse. Getestet in `test/feld-versionen.test.ts`.
 */

/** Eine Zeile aus `GET /api/events/:id/field-versions` — strukturell kompatibel mit
 *  `EventFieldVersionRow` (`packages/db/src/schema.ts`), hier eigenstaendig deklariert (kein
 *  Backend-Import aus `lib/*`, dasselbe Prinzip wie `AuftragVerlaufActivityEntry` in
 *  `lib/auftrag-verlauf.ts`). */
export interface FeldVersionZeile {
  id: string;
  field: string;
  version: number;
  oldValue: unknown;
  newValue: unknown;
  source: "create" | "patch" | "restore" | "transition" | "customer_merge";
  actorType: "user" | "customer" | "system";
  actorLabel: string | null;
  actorId: string | null;
  changedAt: string;
}

/**
 * Die Feldmenge der Eckdaten-Karte (Datum/Uhrzeit/Gaeste/Ort/Ort-Typ/Aufbau) — der Drawer "Aenderungen
 * an den Eckdaten" zeigt NUR diese, obwohl `GET /api/events/:id/field-versions` (ohne `field`-Filter)
 * alle ~35 versionierten Vorgangsfelder liefert (u. a. `customerId`/`menuSelection`, die anderswo
 * editiert werden). Spiegel der Felder in `AuftragEckdatenKarte.tsx`.
 */
export const ECKDATEN_FELDER = [
  "eventDate",
  "dateEnd",
  "timeFrom",
  "timeTo",
  "guestCount",
  "location",
  "locationType",
  "offSiteMode",
  "setupLocation",
] as const;
export type EckdatenFeld = (typeof ECKDATEN_FELDER)[number];

/**
 * Termin/Gaeste/Ort/Ort-Typ — dieselbe Feldmenge, die frueher `CONTRACT_LOCKED_FIELDS`
 * (`apps/api/src/routes/events.ts`, vor PR3 entfernt) fuer GENAU DIESE Karte kannte. `setupLocation`
 * war dort laut Kopfkommentar BEWUSST NIE enthalten ("rein logistisches Feld, kein
 * Vertragsbestandteil") und bleibt darum auch hier aussen vor — ein Restore auf `setupLocation`
 * braucht daher NIE die Modal-Bestaetigung aus Plan Abschnitt 4.3, unabhaengig vom Vorgangsstatus.
 */
export const VERTRAGSRELEVANTE_ECKDATEN_FELDER = [
  "eventDate",
  "dateEnd",
  "timeFrom",
  "timeTo",
  "guestCount",
  "location",
  "locationType",
  "offSiteMode",
] as const;

export function istVertragsrelevantesFeld(field: string): boolean {
  return (VERTRAGSRELEVANTE_ECKDATEN_FELDER as readonly string[]).includes(field);
}

/** Spiegel von `BOUND_STATUSES` (`apps/api/src/lib/event-status.ts`) — EINE Quelle statt der
 *  frueheren Kopie direkt in `AuftragEckdatenKarte.tsx` (dort bis PR4 lokal definiert). */
export const BOUND_STATUSES = [
  "offer_chosen",
  "paid",
  "payment_failed",
  "balance_due",
  "completed",
  "cancelled",
] as const;

export function istGebundenerVorgang(status: string): boolean {
  return (BOUND_STATUSES as readonly string[]).includes(status);
}

/** Deutsche Feldbezeichnung fuer Drawer-Zeilen, Sammelzeilen-Titel ("Gaeste, Datum") und
 *  Zuruecksetzen-Knopftexte. */
export const FELD_LABEL: Record<string, string> = {
  eventDate: "Datum",
  dateEnd: "Enddatum",
  timeFrom: "Uhrzeit (Beginn)",
  timeTo: "Uhrzeit (Ende)",
  guestCount: "Gäste",
  location: "Ort",
  locationType: "Ort-Typ",
  offSiteMode: "Catering oder Abholung",
  setupLocation: "Aufbau",
};

export function feldLabel(field: string): string {
  return FELD_LABEL[field] ?? field;
}

// Dieselben Anzeige-Texte wie die Select-Optionen in `AuftragEckdatenKarte.tsx`
// (`LOCATION_TYPE_OPTIONS`/`OFF_SITE_MODE_OPTIONS`/`SETUP_LOCATION_OPTIONS`) — bewusst hier
// dupliziert statt importiert (jene Konstanten tragen zusaetzlich einen Platzhalter-Eintrag
// "Nicht gepflegt" fuer den leeren Select-Zustand, der fuer die reine Wert-FORMATIERUNG hier nicht
// passt) - Aenderung an einer Stelle MUSS an der anderen nachgezogen werden, dasselbe bewusste
// "Spiegel"-Muster wie `BOUND_STATUSES`/`OFFER_LOCKED_STATUSES` im Bestand.
const LOCATION_TYPE_LABEL: Record<string, string> = { in_house: "Im Haus · Event", off_site: "Außer Haus" };
const OFF_SITE_MODE_LABEL: Record<string, string> = { catering: "Außer Haus · Catering", delivery: "Abholung" };
const SETUP_LOCATION_LABEL: Record<string, string> = { indoor: "Innen", outdoor: "Außen", mixed: "Beides" };

/** "2026-11-11" -> "11.11.2026"; alles andere (nicht erkanntes Format) faellt unveraendert durch. */
function formatDatum(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** Ein Feldwert (`oldValue`/`newValue` aus `event_field_versions`) als kurzer, lesbarer Text —
 *  Grundlage sowohl fuer `WertAenderung` (Diff am Feld/in der Drawer-Zeile) als auch fuer den
 *  Zuruecksetzen-Knopftext ("Auf 80 zurücksetzen", Plan Abschnitt 4.3: der Knopf traegt den Wert). */
export function formatiereFeldWert(field: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "–";
  if ((field === "eventDate" || field === "dateEnd") && typeof value === "string") return formatDatum(value);
  if (field === "locationType" && typeof value === "string") return LOCATION_TYPE_LABEL[value] ?? value;
  if (field === "offSiteMode" && typeof value === "string") return OFF_SITE_MODE_LABEL[value] ?? value;
  if (field === "setupLocation" && typeof value === "string") return SETUP_LOCATION_LABEL[value] ?? value;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function zeitMs(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** "heute, 20:17 Uhr" / "14.09.2026, 20:17 Uhr" — geteiltes Zeitformat fuer den "Geändert nach
 *  Zusage"-Hinweis in `AuftragEckdatenKarte.tsx` UND die Drawer-Zeilen in `FeldVersionenDrawer.tsx`. */
export function formatiereZeitpunkt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  const heute = new Date();
  const istHeute = d.toDateString() === heute.toDateString();
  const uhrzeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  if (istHeute) return `heute, ${uhrzeit} Uhr`;
  return `${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}, ${uhrzeit} Uhr`;
}

/** Regel A, Zeitfenster: 5 Minuten (Plan Abschnitt 4.2). */
const TIPPKORREKTUR_FENSTER_MS = 5 * 60_000;
/** Regel C, Zeitfenster: 30 Minuten (Plan Abschnitt 4.2). */
const BUENDELUNG_FENSTER_MS = 30 * 60_000;
/** Regel D, Zeilenlimit im Drawer (Plan Abschnitt 4.2). */
export const FELD_VERSIONEN_DRAWER_LIMIT = 50;

export interface FeldVersionKollaps {
  /** Die angezeigte Zeile: Akteur/Zeitpunkt/`newValue` der LETZTEN Aenderung der Serie,
   *  `oldValue` der ERSTEN — ein Tippkorrektur-Burst A -> B -> C -> D zeigt sich als EINE Zeile
   *  A -> D. `id` bleibt die der letzten (echten) Versionszeile. */
  zeile: FeldVersionZeile;
  /** Die kollabierten Zwischenschritte (aelteste zuerst), OHNE die angezeigte letzte Zeile — leer,
   *  wenn keine Kollabierung stattfand. Bleiben ueber "N Zwischenschritte anzeigen" abrufbar (die
   *  Datenbank bleibt lueckenlos, nur die STANDARD-Anzeige verdichtet). */
  zwischenschritte: FeldVersionZeile[];
  /**
   * Die Versionszeile, deren `newValue` dem angezeigten `oldValue` DIESER Kollaps-Zeile entspricht
   * — genau die Zeile, die `POST .../field-versions/:versionId/restore` adressieren MUSS, um den
   * Zurücksetzen-Knopf "Auf {alter Wert} zurücksetzen" (Plan Abschnitt 4.3, freigegebenes Mockup)
   * korrekt umzusetzen: der Restore-Endpunkt wendet immer die `newValue` der referenzierten Zeile
   * an, NIE ihre `oldValue` — restaurieren "auf den alten Wert dieser Zeile" heisst technisch also
   * "restaurieren auf die VORHERIGE Zeile dieses Feldes". `null`, wenn die Gruppe mit der
   * ALLERERSTEN Version des Feldes beginnt (`source: "create"`, `oldValue` bereits `null`/leer) —
   * dort gibt es keinen frueheren Zustand, zu dem zurueckgesetzt werden koennte, also KEIN Knopf
   * (bewusste Abweichung vom Mockup, das dort demonstrativ trotzdem einen Knopf zeigt — ein
   * Knopf, der serverseitig ins Leere liefe, waere ein stiller Fehlerpfad).
   */
  vorherigeVersion: FeldVersionZeile | null;
}

/** `"<field>:<version>"` -> Zeile, aus dem KOMPLETTEN geladenen Bestand (nicht nur einer Phase) —
 *  Grundlage fuer `vorherigeVersion` oben: Version N-1 desselben Feldes. */
function baueVersionsIndex(alleZeilen: FeldVersionZeile[]): Map<string, FeldVersionZeile> {
  const index = new Map<string, FeldVersionZeile>();
  for (const z of alleZeilen) index.set(`${z.field}:${z.version}`, z);
  return index;
}

/**
 * Regel A: fasst pro FELD (nicht wie `fasseBurstsZusammen` pro Aktion+Datensatz-ID) aufeinander
 * folgende Aenderungen mit <= 5 Minuten Abstand zu einer Kollaps-Zeile zusammen. Verschiedene
 * Felder kollabieren nie miteinander, auch wenn sie im selben PATCH entstanden — jedes Feld hat
 * seine eigene Versionslinie (Plan Abschnitt 1: "version zaehlt PRO FELD").
 *
 * `versionsIndex` (optional): der vollstaendige `baueVersionsIndex(...)`-Bestand ueber ALLE
 * geladenen Zeilen (auch aus der jeweils ANDEREN Phase) — noetig, damit `vorherigeVersion` eine
 * Vorgaenger-Version auch dann findet, wenn sie in der Erfassungsphase liegt, waehrend `zeilen`
 * hier nur den "danach"-Teil enthaelt. Ohne Angabe wird der Index nur aus `zeilen` selbst gebaut
 * (fuer isolierte Tests/Aufrufe ausreichend).
 */
export function kollabiereTippkorrekturen(
  zeilen: FeldVersionZeile[],
  versionsIndex?: Map<string, FeldVersionZeile>,
): FeldVersionKollaps[] {
  const index = versionsIndex ?? baueVersionsIndex(zeilen);
  const proFeld = new Map<string, FeldVersionZeile[]>();
  for (const z of zeilen) {
    const liste = proFeld.get(z.field);
    if (liste) liste.push(z);
    else proFeld.set(z.field, [z]);
  }

  const ergebnis: FeldVersionKollaps[] = [];
  for (const liste of proFeld.values()) {
    const sortiert = [...liste].sort((a, b) => zeitMs(a.changedAt) - zeitMs(b.changedAt));
    let gruppe: FeldVersionZeile[] = [];
    for (const z of sortiert) {
      const vorheriges = gruppe[gruppe.length - 1];
      if (vorheriges && zeitMs(z.changedAt) - zeitMs(vorheriges.changedAt) <= TIPPKORREKTUR_FENSTER_MS) {
        gruppe.push(z);
      } else {
        if (gruppe.length > 0) ergebnis.push(baueKollaps(gruppe, index));
        gruppe = [z];
      }
    }
    if (gruppe.length > 0) ergebnis.push(baueKollaps(gruppe, index));
  }
  return ergebnis.sort((a, b) => zeitMs(b.zeile.changedAt) - zeitMs(a.zeile.changedAt));
}

function baueKollaps(gruppe: FeldVersionZeile[], index: Map<string, FeldVersionZeile>): FeldVersionKollaps {
  const erste = gruppe[0]!;
  const letzte = gruppe[gruppe.length - 1]!;
  const vorherigeVersion = index.get(`${erste.field}:${erste.version - 1}`) ?? null;
  return { zeile: { ...letzte, oldValue: erste.oldValue }, zwischenschritte: gruppe.slice(0, -1), vorherigeVersion };
}

/**
 * Regel B: teilt die rohen Zeilen an der Grenze der ersten GESENDETEN Angebotsversion.
 * `ersteAngebotsversionGesendetAm` kommt vom Aufrufer (`offer_history.sent_at`, kein Bezug in
 * `event_field_versions` selbst) — `null`, wenn noch nie ein Angebot gesendet wurde: dann gilt der
 * gesamte bisherige Vorgang als Erfassungsphase (es gibt noch keine Grenze, ab der "danach" gelten
 * koennte).
 */
export function teileErfassungsphase(
  zeilen: FeldVersionZeile[],
  ersteAngebotsversionGesendetAm: string | null,
): { erfassungsphase: FeldVersionZeile[]; danach: FeldVersionZeile[] } {
  if (!ersteAngebotsversionGesendetAm) return { erfassungsphase: zeilen, danach: [] };
  const grenze = zeitMs(ersteAngebotsversionGesendetAm);
  const erfassungsphase = zeilen.filter((z) => zeitMs(z.changedAt) < grenze);
  const danach = zeilen.filter((z) => zeitMs(z.changedAt) >= grenze);
  return { erfassungsphase, danach };
}

/** Regel D: die ersten `limit` Zeilen sichtbar, Rest hinter "Ältere anzeigen". */
export function begrenzeFuerDrawer<T>(
  zeilen: T[],
  limit: number = FELD_VERSIONEN_DRAWER_LIMIT,
): { sichtbar: T[]; hatMehr: boolean } {
  if (zeilen.length <= limit) return { sichtbar: zeilen, hatMehr: false };
  return { sichtbar: zeilen.slice(0, limit), hatMehr: true };
}

export interface FeldVersionenDrawerAnsicht {
  /** Regel A angewendet, absteigend sortiert, Regel D begrenzt (Erfassungsphase zaehlt NICHT mit
   *  — sie ist ohnehin eingeklappt, kein Platzproblem). */
  danach: FeldVersionKollaps[];
  /** Regel A angewendet, absteigend sortiert — der komplette Inhalt des eingeklappten Blocks
   *  "Während der Erfassung · N Änderungen". */
  erfassungsphase: FeldVersionKollaps[];
  /** true, wenn `danach` durch Regel D gekappt wurde ("Ältere anzeigen"-Knopf noetig). */
  hatMehr: boolean;
}

/** Baut die vollstaendige Drawer-Ansicht aus den rohen API-Zeilen: Regeln A, B und D kombiniert.
 *  Regel C (Verlaufs-Karten-Sammelzeile) ist bewusst NICHT Teil dieser Funktion — sie hat ein
 *  anderes Ziel-Format (`FeldVersionBundle`, s. u.), nicht `FeldVersionKollaps`. */
export function bereiteDrawerAn(
  zeilen: FeldVersionZeile[],
  ersteAngebotsversionGesendetAm: string | null,
  /** Regel D, ueberschreibbar fuer "Ältere anzeigen" (der Aufrufer haelt bereits mehr Rohzeilen
   *  geladen, als initial angezeigt werden — ein Klick erhoeht schlicht diesen Wert, kein
   *  zusaetzlicher Request noetig, solange der Server-seitige Fetch grosszuegig genug war). */
  limit: number = FELD_VERSIONEN_DRAWER_LIMIT,
): FeldVersionenDrawerAnsicht {
  // EIN gemeinsamer Versionsindex ueber ALLE geladenen Zeilen (beide Phasen) — eine Gruppe im
  // "danach"-Teil kann ihre Vorgaengerversion (`vorherigeVersion`) in der Erfassungsphase haben.
  const index = baueVersionsIndex(zeilen);
  const { erfassungsphase, danach } = teileErfassungsphase(zeilen, ersteAngebotsversionGesendetAm);
  const { sichtbar, hatMehr } = begrenzeFuerDrawer(kollabiereTippkorrekturen(danach, index), limit);
  return { danach: sichtbar, erfassungsphase: kollabiereTippkorrekturen(erfassungsphase, index), hatMehr };
}

/** Regel C: eine verdichtete Sammelzeile fuer die Verlaufs-Karte. */
export interface FeldVersionBundle {
  key: string;
  /** Zeitpunkt der letzten Aenderung im Buendel — die Sammelzeile sortiert sich daran ein. */
  zeitpunkt: string;
  /** Betroffene Felder, in der Reihenfolge ihrer ERSTEN Aenderung im Buendel (nicht alphabetisch —
   *  "Gäste, Datum" statt "Datum, Gäste", wenn Gäste zuerst geaendert wurde). Jedes Feld hoechstens
   *  einmal, auch wenn es im Buendel mehrfach geaendert wurde. */
  felder: string[];
  actorType: "user" | "customer" | "system";
  actorLabel: string | null;
}

/**
 * Regel C: buendelt Aenderungen NACH der Erfassungsphase (Regel B faengt die Erfassungsphase vorher
 * ab — "die Erfassungsphase erzeugt dort NULL Zeilen") nach Akteur + <= 30 Minuten Abstand zu je
 * EINER Sammelzeile "Eckdaten geändert · Gäste, Datum · durch X" fuer die Verlaufs-Karte.
 */
export function buendleFuerVerlaufskarte(
  zeilen: FeldVersionZeile[],
  ersteAngebotsversionGesendetAm: string | null,
): FeldVersionBundle[] {
  const { danach } = teileErfassungsphase(zeilen, ersteAngebotsversionGesendetAm);
  const sortiert = [...danach].sort((a, b) => zeitMs(a.changedAt) - zeitMs(b.changedAt));

  interface Laufend {
    key: string;
    zeitpunkt: string;
    felder: string[];
    actorType: "user" | "customer" | "system";
    actorLabel: string | null;
    actorKey: string;
  }
  const ergebnis: FeldVersionBundle[] = [];
  let laufend: Laufend | null = null;
  for (const z of sortiert) {
    const actorKey = `${z.actorType}:${z.actorId ?? z.actorLabel ?? ""}`;
    if (laufend && laufend.actorKey === actorKey && zeitMs(z.changedAt) - zeitMs(laufend.zeitpunkt) <= BUENDELUNG_FENSTER_MS) {
      laufend.zeitpunkt = z.changedAt;
      if (!laufend.felder.includes(z.field)) laufend.felder.push(z.field);
    } else {
      if (laufend) ergebnis.push(laufend);
      laufend = { key: z.id, zeitpunkt: z.changedAt, felder: [z.field], actorType: z.actorType, actorLabel: z.actorLabel, actorKey };
    }
  }
  if (laufend) ergebnis.push(laufend);
  return ergebnis.sort((a, b) => zeitMs(b.zeitpunkt) - zeitMs(a.zeitpunkt));
}
